import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';

import RevampMenuSelect from 'components/revamp/RevampMenuSelect';
import { font, fontSize, radius, shadow, space, text as textToken } from 'styles/revampTokens';
import {
  revampFieldInputStyles,
  revampFieldLabelStyles,
  revampModalCloseButtonStyles,
  revampModalFooterStyles,
  revampModalPrimaryButtonStyles,
  revampModalSecondaryButtonStyles,
} from 'styles/revampFormFieldStyles';

import {
  createDelivery,
  DeliveryRecord,
  DeliveryRunRecord,
  listDeliveryRuns,
  maestroErrorMessage,
  testDelivery,
  updateDelivery,
} from './maestroDeliveryClient';
import { validateDeliverySave } from './deliveryFormValidation';
import {
  DEFAULT_DELIVERY_SCHEDULE_CRON,
  DELIVERY_SCHEDULE_PRESET_CRONS,
  DELIVERY_SCHEDULE_PRESETS,
  legacyWeeklyToCron,
  resolveScheduleCron,
  schedulePayloadFromCron,
} from './deliverySchedulePresets';

import type { SnapshotCreateBody } from './types';

interface DeliveryDialogProperties {
  isOpen: boolean;
  onClose: () => void;
  modelId: number;
  dashboardId: string;
  dashboardName?: string;
  parameterSet: SnapshotCreateBody;
  existing?: DeliveryRecord | null;
  /** Other saved schedules for this dashboard — enables in-dialog switching. */
  schedules?: DeliveryRecord[];
  onSwitchSchedule?: (deliveryId: string | null) => void;
  onSaved?: (delivery: DeliveryRecord) => void;
}

/** Parse comma/semicolon/space separated emails into maestro recipient rows. */
export function parseEmailRecipients(raw: string): Array<{ type: 'email'; address: string }> {
  return raw
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter((part) => part.includes('@'))
    .map((address) => ({ type: 'email' as const, address }));
}

/**
 * Schedule / save a report pack delivery and run Test now via maestro.
 */
const DeliveryDialog = ({
  isOpen,
  onClose,
  modelId,
  dashboardId,
  dashboardName,
  parameterSet,
  existing,
  schedules,
  onSwitchSchedule,
  onSaved,
}: DeliveryDialogProperties) => {
  const toast = useToast();
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'draft' | 'active' | 'paused'>('draft');
  const [scheduleCron, setScheduleCron] = useState(DEFAULT_DELIVERY_SCHEDULE_CRON);
  const [recipientEmails, setRecipientEmails] = useState('');
  const [deliveryId, setDeliveryId] = useState<string | null>(existing?.id ?? null);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [runs, setRuns] = useState<DeliveryRunRecord[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setName(existing?.name ?? dashboardName ?? 'Report pack delivery');
    setStatus(existing?.status ?? 'draft');
    setScheduleCron(resolveScheduleCron(existing?.schedule) || DEFAULT_DELIVERY_SCHEDULE_CRON);
    setDeliveryId(existing?.id ?? null);
    const emails =
      existing?.recipients
        ?.filter((r) => r.type === 'email' && r.address)
        .map((r) => r.address)
        .join(', ') ?? '';
    setRecipientEmails(emails);
  }, [isOpen, existing, dashboardName]);

  const loadRuns = useCallback(async (id: string) => {
    try {
      const history = await listDeliveryRuns(id);
      setRuns(Array.isArray(history) ? history : []);
    } catch {
      setRuns([]);
    }
  }, []);

  useEffect(() => {
    if (deliveryId && isOpen) loadRuns(deliveryId);
  }, [deliveryId, isOpen, loadRuns]);

  const recipients = useMemo(() => parseEmailRecipients(recipientEmails), [recipientEmails]);

  const buildBody = () => ({
    model_id: modelId,
    name: name.trim(),
    status,
    source_ref: { service: 'blox-bi' as const, type: 'dashboard' as const, id: dashboardId },
    parameter_set: parameterSet,
    output_types: ['pdf'],
    recipients,
    schedule: schedulePayloadFromCron(scheduleCron),
  });

  const save = async () => {
    const validationError = validateDeliverySave(status, recipients);
    if (validationError) {
      toast({ status: 'error', title: 'Cannot save', description: validationError });
      return;
    }
    setBusy('save');
    try {
      const body = buildBody();
      const saved = deliveryId
        ? await updateDelivery(deliveryId, body)
        : await createDelivery(body);
      setDeliveryId(saved.id);
      onSaved?.(saved);
      toast({ status: 'success', title: 'Delivery saved' });
      await loadRuns(saved.id);
    } catch (error) {
      toast({
        status: 'error',
        title: 'Could not save delivery',
        description: maestroErrorMessage(error, 'Save failed'),
      });
    } finally {
      setBusy(null);
    }
  };

  const runTest = async () => {
    const validationError = validateDeliverySave(status, recipients);
    if (validationError && status === 'active') {
      toast({ status: 'error', title: 'Cannot run test', description: validationError });
      return;
    }
    setBusy('test');
    try {
      let id = deliveryId;
      if (!id) {
        const saved = await createDelivery(buildBody());
        id = saved.id;
        setDeliveryId(id);
        onSaved?.(saved);
      }
      const run = await testDelivery(id);
      toast({
        status: run.status === 'succeeded' ? 'success' : 'error',
        title: run.status === 'succeeded' ? 'Test email sent' : 'Test now failed',
        description: run.error ?? 'Check your inbox for the PDF.',
      });
      await loadRuns(id);
    } catch (error) {
      toast({
        status: 'error',
        title: 'Test now failed',
        description: maestroErrorMessage(error, 'Test failed'),
      });
      if (deliveryId) await loadRuns(deliveryId);
    } finally {
      setBusy(null);
    }
  };

  const lastRun = runs.length > 0 ? runs[0] : undefined;
  const legacyCron = existing?.schedule ? legacyWeeklyToCron(existing.schedule) : '';

  const scheduleExtraItems = (() => {
    const extras: { value: string; label: string }[] = [];
    const seen = new Set<string>();
    const addCustom = (cron: string) => {
      if (!cron || DELIVERY_SCHEDULE_PRESET_CRONS.has(cron) || seen.has(cron)) return;
      seen.add(cron);
      extras.push({ value: cron, label: `Custom: ${cron}` });
    };
    addCustom(legacyCron);
    addCustom(scheduleCron);
    return extras;
  })();

  const scheduleGroups = (['Daily', 'Weekly', 'Monthly'] as const).map((group) => ({
    label: group,
    items: DELIVERY_SCHEDULE_PRESETS.filter((row) => row.group === group).map((row) => ({
      value: row.cron,
      label: row.label,
    })),
  }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent
        width='520px'
        maxWidth='92vw'
        p='20px'
        borderRadius={radius.surface}
        boxShadow={shadow.overlay}
        fontFamily={font.body}
      >
        <ModalHeader
          p={0}
          pb={space['2xl']}
          mb={space.md}
          fontFamily={font.display}
          fontSize='18px'
          fontWeight={600}
          color={textToken.primary}
        >
          Schedule delivery
        </ModalHeader>
        <ModalCloseButton top='16px' right='16px' {...revampModalCloseButtonStyles} />
        <ModalBody p={0}>
          <Stack spacing={4}>
            {schedules && schedules.length > 0 && onSwitchSchedule && (
              <FormControl>
                <FormLabel {...revampFieldLabelStyles}>Editing</FormLabel>
                <RevampMenuSelect
                  value={existing?.id ?? '__new__'}
                  onChange={(value) => onSwitchSchedule(value === '__new__' ? null : value)}
                  items={[
                    { value: '__new__', label: 'New schedule' },
                    ...schedules.map((row) => ({
                      value: row.id,
                      label: `${row.name || row.id} (${row.status})`,
                    })),
                  ]}
                />
              </FormControl>
            )}
            <FormControl>
              <FormLabel htmlFor='pack-delivery-name' {...revampFieldLabelStyles}>
                Name
              </FormLabel>
              <Input
                id='pack-delivery-name'
                value={name}
                onChange={(event) => setName(event.target.value)}
                {...revampFieldInputStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel id='pack-delivery-status-label' {...revampFieldLabelStyles}>
                Status
              </FormLabel>
              <RevampMenuSelect
                aria-labelledby='pack-delivery-status-label'
                value={status}
                onChange={(value) => setStatus(value as typeof status)}
                items={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                ]}
              />
            </FormControl>
            <FormControl>
              <FormLabel id='pack-delivery-schedule-label' {...revampFieldLabelStyles}>
                Schedule (UTC)
              </FormLabel>
              <RevampMenuSelect
                aria-labelledby='pack-delivery-schedule-label'
                value={scheduleCron}
                onChange={setScheduleCron}
                items={scheduleExtraItems}
                groups={scheduleGroups}
              />
              <Text mt={1} fontSize={fontSize.label} color={textToken.secondary}>
                Same preset list as Dataflow connection schedules. Unattended runs ship in a later
                release; Test now sends immediately.
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel htmlFor='pack-delivery-recipients' {...revampFieldLabelStyles}>
                Recipients
              </FormLabel>
              <Input
                id='pack-delivery-recipients'
                value={recipientEmails}
                onChange={(event) => setRecipientEmails(event.target.value)}
                placeholder='cfo@example.com, finance@example.com'
                {...revampFieldInputStyles}
              />
              <Text mt={1} fontSize={fontSize.label} color={textToken.secondary}>
                Comma-separated email addresses. Test now sends only to you; saved emails apply to
                the schedule.
              </Text>
            </FormControl>
            <Text fontSize={fontSize.label} color={textToken.secondary}>
              Output: PDF. Pack contents match the scenario and reporting period shown in the export
              dialog.
            </Text>
            {lastRun && (
              <Box fontSize={fontSize.label} color={textToken.secondary}>
                Last run: {lastRun.status} at {new Date(lastRun.started_at).toLocaleString()}
                {lastRun.error ? ` — ${lastRun.error}` : ''}
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter
          {...revampModalFooterStyles}
          borderTop='none'
          pt={0}
          justifyContent='space-between'
          alignItems='center'
          gap={space.lg}
        >
          <Button
            variant='outline'
            onClick={runTest}
            isLoading={busy === 'test'}
            isDisabled={busy !== null}
            {...revampModalSecondaryButtonStyles}
          >
            Test now
          </Button>
          <Flex gap={space.lg} flexWrap='wrap' justifyContent='flex-end'>
            <Button variant='outline' onClick={onClose} {...revampModalSecondaryButtonStyles}>
              Close
            </Button>
            <Button
              onClick={save}
              isLoading={busy === 'save'}
              isDisabled={busy !== null}
              {...revampModalPrimaryButtonStyles}
            >
              Save
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeliveryDialog;
